import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

import * as Fonts from "../../components/Fonts";
import { KeepoLoader } from "../../components/KeepoLoader";
import { useToast } from "../../components/Toast";
import { isApiError } from "../../services/api/errors";
import { FeesPayer, PaymentsState, updatePayments } from "../../services/payments";
import { formatMoney } from "../../utils/money";
import { Card, Section } from "../Settings/styles";
import { chargesPrefix, PaymentsLoad, prefix } from "./state";
import { Center, ErrorMessage, Line, Message, Option, Options } from "./styles";

type Props = {
  siteId: string;
  payments: PaymentsLoad;
  setPayments: (state: PaymentsState) => void;
  isCurrent: (siteId: string) => boolean;
};

/** Taxas e prazos: who pays the fees (with the API's example), each method's fee and when its money is available. */
export const FeesAndTerms = ({ siteId, payments, setPayments, isCurrent }: Props) => {
  const { t, i18n } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<"notApproved" | "generic">();
  const toast = useToast();
  const money = (cents: number) => formatMoney(cents, "brl", i18n.language);

  if (payments === undefined) {
    return (
      <Section>
        <Card role="status" aria-label={t(`${prefix}.loading`)}>
          <Center>
            <KeepoLoader size={24} color="var(--color-neutral-white)" />
          </Center>
        </Card>
      </Section>
    );
  }
  if (payments === "unavailable" || payments === "error" || !payments.eligible) {
    const message = typeof payments === "string" ? payments : "notEligible";
    return (
      <Section>
        <Card>
          <Message>{t(`${prefix}.${message === "error" ? "loadError" : message}`)}</Message>
        </Card>
      </Section>
    );
  }

  const { feesPayer } = payments.account;
  const { example, fees } = payments;

  // Not optimistic: the choice waits for the API and keeps the saved value if it fails
  const choose = async (value: FeesPayer) => {
    if (value === feesPayer) return;
    const site = siteId;
    setError(undefined);
    setSaving(true);
    try {
      const state = await updatePayments(site, { feesPayer: value });
      if (isCurrent(site)) setPayments(state);
      toast.success(t(`${chargesPrefix}.payer.saved.${value}`));
    } catch (failure) {
      if (isCurrent(site)) setError(isApiError(failure, "RECIPIENT_NOT_APPROVED") ? "notApproved" : "generic");
    } finally {
      setSaving(false);
    }
  };

  /** "3,1% + R$ 1,20", "R$ 0,65" or "3,1%" */
  const fee = ({ pctBp, fixedCents }: { pctBp: number; fixedCents: number }) => {
    const pct = `${(pctBp / 100).toLocaleString(i18n.language, { maximumFractionDigits: 2 })}%`;
    if (pctBp && fixedCents) return `${pct} + ${money(fixedCents)}`;
    return pctBp ? pct : money(fixedCents);
  };

  // each method the API sends a fee for (Pix and card; boleto too, where a front sells by it), in this order
  const methods = (["pix", "card", "boleto"] as const).filter(method => method in fees);
  /** When a sale's money can be withdrawn */
  const term = (method: (typeof methods)[number]) =>
    method === "card"
      ? t(`${chargesPrefix}.fees.cardTerm`, { count: payments.cardReleaseDays })
      : t(`${chargesPrefix}.fees.${method}Term`);

  const examples = {
    owner: { price: money(example.priceCents), pix: money(example.owner.pix), card: money(example.owner.card) },
    customer: {
      price: money(example.priceCents),
      pix: money(example.customer.pix),
      card: money(example.customer.card),
    },
  };

  return (
    <>
      <Section>
        <Fonts.Label>{t(`${prefix}.charging.feesPayer`)}</Fonts.Label>
        <Options role="radiogroup" aria-label={t(`${prefix}.charging.feesPayer`)}>
          {(["owner", "customer"] as const).map(value => (
            <Option
              key={value}
              type="button"
              role="radio"
              aria-checked={feesPayer === value}
              disabled={saving}
              onClick={() => choose(value)}>
              <span className="dot" aria-hidden="true" />
              <span className="text">
                <span className="title">{t(`${chargesPrefix}.payer.${value}.title`)}</span>
                <span className="description">{t(`${chargesPrefix}.payer.${value}.example`, examples[value])}</span>
              </span>
            </Option>
          ))}
        </Options>
        {error && <ErrorMessage role="alert">{t(`${prefix}.charging.errors.${error}`)}</ErrorMessage>}
      </Section>

      <Section>
        <Fonts.Label>{t(`${chargesPrefix}.fees.perSale`)}</Fonts.Label>
        <Card>
          {methods.map((method, index) => (
            <Fragment key={method}>
              {index > 0 && <div className="divider" />}
              <Line>
                <span className="text">
                  <span className="name">{t(`${chargesPrefix}.fees.${method}`)}</span>
                  <span>{term(method)}</span>
                </span>
                <strong>{fee(fees[method])}</strong>
              </Line>
            </Fragment>
          ))}
        </Card>
      </Section>

      <Section>
        <Fonts.Label>{t(`${chargesPrefix}.fees.withdrawal`)}</Fonts.Label>
        <Card>
          <Line>
            <span>{t(`${chargesPrefix}.fees.withdrawalFee`)}</span>
            <strong>{money(payments.withdrawFeeCents)}</strong>
          </Line>
          <div className="divider" />
          <Line>
            <span>{t(`${chargesPrefix}.fees.withdrawalMinimum`)}</span>
            <strong>{money(payments.minWithdrawCents)}</strong>
          </Line>
        </Card>
      </Section>

      <Section>
        <Fonts.Label>{t(`${chargesPrefix}.fees.online`)}</Fonts.Label>
        <Card>
          <Line>
            <span>{t(`${chargesPrefix}.fees.minSale`)}</span>
            <strong>{money(payments.minSaleCents)}</strong>
          </Line>
          <Message>{t(`${chargesPrefix}.fees.minSaleHint`)}</Message>
        </Card>
      </Section>
    </>
  );
};
